<template>
  <div class="layout">
    <aside class="sidebar">
      <div class="top-icons">
        <button class="icon-btn">
            <img :src="logoImg" class="brand-logo" />
          </button>
        

        <router-link to="/manager/bots" class="icon-link">
          <button class="icon-btn">
            <Workflow :size="18" />
          </button>
        </router-link>
        <router-link to="/manager/broadcastcampaigns" class="icon-link">
          <button class="icon-btn">
            <Calendar :size="18" />
          </button>
        </router-link>
        <router-link to="/manager/channel-settings" class="icon-link">
          <button class="icon-btn">
            <Settings :size="18" />
          </button>
        </router-link>
        <router-link to="/manager/broadcastcampaignsreport" class="icon-link">
          <button class="icon-btn">
            <ChartPie :size="18" />
          </button>
        </router-link>

        <!-- <router-link to="/manager/dashboard" class="icon-link">
          <button class="icon-btn">
            
            <LayoutDashboard :size="18" />
            
          </button>
        </router-link> -->
      </div>

      <div class="bottom-icons">
        <div class="avatar">
          {{ userInitials }}
        </div>
        <button class="icon-btn">◐</button>
      </div>
    </aside>

    <div class="main-area">
      <header class="top">
        <div>
          <b>Manager Portal</b>
          <small> — {{ user?.username }} ({{ user?.role }})</small>
        </div>
        <div class="actions">
          <button @click="refreshAll">Refresh</button>
          <button class="logout" @click="logout">Logout</button>
          <!-- <button @click="$router.push('/manager/bots')">Flows</button> -->
        </div>
      </header>

      <main class="grid">
        <section class="card">
          <div class="convs">
            <div class="tabs">
              <button class="tab" :class="{ on: filter === 'OPEN' }" @click="setFilter('OPEN')">OPEN</button>
              <button class="tab" :class="{ on: filter === 'QUEUED' }" @click="setFilter('QUEUED')">QUEUED</button>
              <button class="tab" :class="{ on: filter === 'CLOSED' }" @click="setFilter('CLOSED')">CLOSED</button>
            </div>

              <!-- {{ conversations }} -->
            <ConversationCard
              v-for="c in conversations"
              :key="c.conversation_id"
              :c="c"
              :agents="agents"
              @view="openReadOnlyChat"
              @assign="assign"
              @close="closeConv"
            />

            <div v-if="!conversations.length" class="muted" style="padding: 10px;">
              No conversations found.
            </div>
          </div>
        </section>

        <section class="card">
          <div class="cardHead">
            <h3>Conversations</h3>
<!-- 
            <button @click="exportChatPDF(chatMessages)">
              Export Chat PDF
            </button> -->
          </div>

          <div v-if="chatModalOpen" class="chatContainer">
            <div class="chatHeader">
              <div>
                <h3 style="margin: 0;">
                  #{{ activeConversation?.conversation_id }}
                </h3>

                <small class="muted">
                  {{ activeConversation?.visitor_name || "Visitor" }}
                  <span v-if="activeConversation?.visitor_phone">
                    • {{ activeConversation.visitor_phone }}
                  </span>
                  <span v-if="activeConversation?.assigned_username">
                    • @{{ activeConversation.assigned_username }}
                  </span>
                  <span v-if="activeConversation?.dispo_status">
                    •  {{ activeConversation.dispo_status }}
                  </span>
                </small>
              </div>

              <!-- <button v-if="activeConversation?.status != 'CLOSED'" class="logout" @click="closeReadOnlyChat">
                Close
              </button> -->
            </div>

            <div class="messages">
              <div v-if="chatLoading" class="muted">Loading messages...</div>

              <div v-else-if="!chatMessages.length" class="muted">
                No messages found.
              </div>

              <div
                v-for="m in chatMessages"
                :key="m.id"
                class="msg"
                :class="messageClass(m)"
              >
  
                <StoryCard
                  v-if="m.sender_type != 'SYSTEM'"
                  :name="messageSender(m)"
                  :date="formatTime(m.created_at)"
                  :message="m.body"
                />

                <div class="readOnlyBar" v-if="m.sender_type == 'SYSTEM'">
                  {{ m.body }}
                  {{ formatTime(m.created_at) }}
                </div>
              </div>
            </div>

            <div v-if="isActiveConversationClosed" class="readOnlyBar">
              This conversation is closed.
            </div>

            <div v-else class="messageComposer">
              <textarea
                v-model="messageText"
                placeholder="Type your message..."
                @keydown.enter.ctrl="sendMessage"
                :disabled="chatLoading || messageSending"
              ></textarea>
              <div class="composerFooter">
                <button
                  @click="sendMessage"
                  :disabled="!canSendManagerMessage"
                  class="send-btn"
                >
                  {{ messageSending ? 'Sending...' : 'Send' }}
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  </div>
</template>

<script setup>
import { Workflow , Calendar, Settings, ChartPie} from 'lucide-vue-next'

import { ref, onMounted, onBeforeUnmount, computed } from "vue";
import { useRouter } from "vue-router";
import { auth } from "@/services/auth";
import { useAgentSocket } from "@/services/socket";
import VARS from "@/services/vars";
import StoryCard from "@/components/StoryCard.vue";
import ConversationCard from "@/components/ConversationCard.vue";
import logoImg from "@/assets/images/logo.png";
// import { LayoutDashboard } from "lucide-vue-next";
// import jsPDF from "jspdf";



const API_BASE = VARS.API_URL;
const router = useRouter();
const user = ref(auth.user);
const { connect, disconnect } = useAgentSocket();

const userInitials = computed(() => {
  const name = user.value?.username || "U";
  return name
    .split(" ")
    .map(v => v[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
});

const agents = ref([]);
const conversations = ref([]);
const filter = ref("OPEN");

const chatModalOpen = ref(false);
const activeConversation = ref(null);
const chatMessages = ref([]);
const chatLoading = ref(false);
const messageText = ref("");
const messageSending = ref(false);
const pendingClientMsgIds = new Set();
let socket = null;

const isActiveConversationClosed = computed(() => (
  activeConversation.value?.status === "CLOSED"
));

const canSendManagerMessage = computed(() => (
  !!activeConversation.value &&
  !isActiveConversationClosed.value &&
  !chatLoading.value &&
  !messageSending.value &&
  !!messageText.value.trim()
));

function logout() {
  if (socket && activeConversation.value) {
    socket.emit("agent:leave_conversation", {
      conversationId: Number(activeConversation.value.conversation_id),
      user: auth.user,
    });
  }
  disconnect();
  auth.clear();
  router.push("/");
}



// function exportChatPDF(chatMessages) {
//   const doc = new jsPDF();

//   let y = 10;

//   chatMessages.forEach((msg) => {
//     const sender =
//       msg.sender_type === "AGENT"
//         ? "Agent"
//         : msg.sender_type === "VISITOR"
//         ? "Customer"
//         : "Bot";

//     const time = new Date(msg.created_at).toLocaleTimeString();

//     const text = `[${time}] ${sender}: ${msg.body}`;

//     const lines = doc.splitTextToSize(text, 180);

//     doc.text(lines, 10, y);
//     y += lines.length * 6;

//     // page break
//     if (y > 280) {
//       doc.addPage();
//       y = 10;
//     }
//   });

//   doc.save(`chat-${Date.now()}.pdf`);
// }



async function apiGet(path) {
  const r = await fetch(API_BASE + path, {
    headers: { Authorization: "Bearer " + auth.token },
  });

  if (r.status === 401) {
    logout();
    throw new Error("401");
  }

  const data = await r.json();
  if (!data.ok) throw new Error(data.message || "API_ERROR");
  return data;
}

async function apiPost(path, body) {
  const r = await fetch(API_BASE + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + auth.token,
    },
    body: JSON.stringify(body || {}),
  });

  if (r.status === 401) {
    logout();
    throw new Error("401");
  }

  const data = await r.json();
  if (!data.ok) throw new Error(data.message || "API_ERROR");
  return data;
}

async function loadAgents() {
  const data = await apiGet("/api/manager/agents");
  agents.value = data.agents || [];
}

async function loadConversations() {
  const data = await apiGet(`/api/manager/conversations?filter=${filter.value}`);
  conversations.value = data.conversations || [];
}

async function refreshAll() {
  await loadAgents();
  await loadConversations();
}

async function setFilter(v) {
  filter.value = v;
  await loadConversations();
}

async function assign(payload) {
  const conversationId = payload?.conversation_id;
  const agentId = payload?.agent_id;
  if (!conversationId || !agentId) return;

  await apiPost(`/api/manager/conversation/${conversationId}/assign`, {
    agentId: Number(agentId),
  });

  await refreshAll();
}

async function closeConv(conversationId) {
  await apiPost(`/api/manager/conversation/${conversationId}/close`, {});
  await refreshAll();
}

async function loadConversationMessages(conversationId) {
  const data = await apiGet(`/api/manager/conversation/${conversationId}/messages`);
  chatMessages.value = data.messages || [];
}

async function openReadOnlyChat(conv) {
  if (activeConversation.value && activeConversation.value.conversation_id !== conv.conversation_id) {
    const s = connect();
    if (s) {
      s.emit("agent:leave_conversation", {
        conversationId: Number(activeConversation.value.conversation_id),
        user: auth.user,
      });
    }
  }

  activeConversation.value = conv;
  chatModalOpen.value = true;
  chatLoading.value = true;
  chatMessages.value = [];
  messageText.value = "";

  const s = connect();
  if (s) {
    socket = s;
    s.emit("agent:join_conversation", {
      conversationId: Number(conv.conversation_id),
      user: auth.user,
    });
  }

  try {
    await loadConversationMessages(conv.conversation_id);
  } catch (e) {
    console.error("Failed to load messages", e);
    chatMessages.value = [];
  } finally {
    chatLoading.value = false;
  }
}

function genClientMsgId() {
  return "c_" + Date.now() + "_" + Math.random().toString(36).slice(2);
}

function pushLocalAgentMessage(text, clientMsgId) {
  chatMessages.value.push({
    id: "local_" + clientMsgId,
    body: text,
    sender_type: "AGENT",
    sender_name: auth.user?.username || "Manager",
    created_at: new Date().toISOString(),
    clientMsgId,
  });
}

function pushRealtimeMessage(msg) {
  const createdAt = msg.created_at || msg.at || new Date();

  chatMessages.value.push({
    id: msg.id || "rt_" + Date.now() + "_" + Math.random().toString(36).slice(2),
    body: msg.body || msg.text || "",
    sender_type: msg.sender_type || msg.senderType || "WHATSAPP",
    sender_name: msg.sender_name || msg.senderName || "",
    created_at: createdAt,
    message_type: msg.message_type || msg.messageType || "text",
    media_id: msg.media_id || msg.mediaId || null,
    clientMsgId: msg.clientMsgId,
  });
}

function sendMessage() {
  if (!canSendManagerMessage.value) return;

  const conversationId = Number(activeConversation.value.conversation_id);
  const text = messageText.value.trim();
  const s = connect();
  if (!s) return;

  socket = s;
  const clientMsgId = genClientMsgId();
  pendingClientMsgIds.add(clientMsgId);

  messageSending.value = true;
  pushLocalAgentMessage(text, clientMsgId);
  messageText.value = "";

  setTimeout(() => {
    if (pendingClientMsgIds.has(clientMsgId)) {
      pendingClientMsgIds.delete(clientMsgId);
      messageSending.value = false;
    }
  }, 5000);

  s.emit("agent:send", {
    conversationId,
    text,
    clientMsgId,
    user: auth.user,
  });
}

async function onChatMessage(msg) {
  const msgConvId = Number(msg.conversation_id || msg.conversationId);

  if (!msgConvId) return;

  if (!activeConversation.value || msgConvId !== Number(activeConversation.value.conversation_id)) {
    await loadConversations();
    return;
  }

  const senderType = msg.sender_type || msg.senderType || "WHATSAPP";

  if (
    senderType === "AGENT" &&
    msg.clientMsgId &&
    pendingClientMsgIds.has(msg.clientMsgId)
  ) {
    pendingClientMsgIds.delete(msg.clientMsgId);
    messageSending.value = false;
    await loadConversations();
    return;
  }

  pushRealtimeMessage(msg);
  await loadConversations();
}

// function closeReadOnlyChat() {
//   chatModalOpen.value = false;
//   activeConversation.value = null;
//   chatMessages.value = [];
// }

function messageSender(m) {
  if (m.sender_type === "AGENT") return m.sender_name || "Agent";
  if (m.sender_type === "VISITOR") return "Visitor";
  if (m.sender_type === "BOT") return "Bot";
  return m.sender_type || "System";
}

function messageClass(m) {
  if (m.sender_type === "AGENT") return "agentMsg";
  if (m.sender_type === "VISITOR") return "visitorMsg";
  if (m.sender_type === "WHATSAPP") return "visitorMsg";
  if (m.sender_type === "BOT") return "botMsg";
  return "systemMsg";
}

function formatTime(v) {
  if (!v) return "";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
}

onMounted(async () => {
  if (!auth.token) return router.push("/");
  if (!["admin", "manager"].includes(auth.user?.role)) return router.push("/inbox");

  socket = connect();
  if (socket) {
    socket.on("chat:message", onChatMessage);
  }

  await refreshAll();
});

onBeforeUnmount(() => {
  if (socket && activeConversation.value) {
    socket.emit("agent:leave_conversation", {
      conversationId: Number(activeConversation.value.conversation_id),
      user: auth.user,
    });
  }
  if (!socket) return;

  socket.off("chat:message", onChatMessage);
});
</script>

<style scoped>
.layout {
  display: flex;
  min-height: 100vh;
  background: #f5f7fb;
  font-family: Arial, sans-serif;
}

.sidebar {
  width: 72px;
  min-width: 72px;
  background: #ffffff;
  border-right: 1px solid #e9edf3;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 14px 10px;
  box-sizing: border-box;
}

.top-icons,
.bottom-icons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.icon-link {
  text-decoration: none;
}

.messageComposer {
  border-top: 1px solid #eef2f7;
  padding: 0;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.messageComposer textarea {
  width: 100%;
  min-height: 100px;
  resize: none;
  border: none;
  outline: none;
  padding: 16px 16px 10px;
  font-size: 14px;
  font-family: inherit;
  font-weight: 500;
  box-sizing: border-box;
}

.messageComposer textarea:disabled {
  background: #f5f7fb;
  opacity: 0.6;
}

.composerFooter {
  display: flex;
  justify-content: flex-end;
  padding: 0 16px 16px;
  gap: 8px;
}

.send-btn {
  min-width: 100px;
  height: 40px;
  border: none;
  border-radius: 8px;
  background: #2f80ed;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  background: #1e5fc5;
  transform: translateY(-1px);
}

.send-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  opacity: 0.6;
}

.grid-btn,
.icon-btn {
  width: 42px;
  height: 42px;
  border: 1px solid #dde3ec;
  border-radius: 12px;
  background: #fff;
  color: #222;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  font-size: 18px;
}

.grid-btn:hover,
.icon-btn:hover {
  background: #f2f6ff;
  border-color: #cfdcff;
}

.icon-btn.active {
  background: #0b66d1;
  color: #fff;
  border-color: #0b66d1;
}

.grid-btn {
  display: grid;
  grid-template-columns: repeat(3, 4px);
  grid-template-rows: repeat(3, 4px);
  gap: 4px;
}

.grid-btn span {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: #333;
  display: block;
}

.avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #0b66d1;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
}

.main-area {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  border-bottom: 1px solid #e9edf3;
  background: #fff;
}

.actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

button {
  padding: 8px 12px;
  border: none;
  border-radius: 10px;
  background: #0b66d1;
  color: #fff;
  cursor: pointer;
}

button.logout {
  background: #444;
}

button.danger {
  background: #e74c3c;
}

button.secondary {
  background: #6c757d;
}

.grid {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 12px;
  padding: 12px;
  flex: 1;
  min-height: 0;
}

.card {
  border: 1px solid #e9edf3;
  border-radius: 14px;
  overflow: hidden;
  background: #fff;
  min-height: 0;
}

.cardHead {
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

h3 {
  margin: 0;
}

.agents,
.convs {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  max-height: calc(100vh - 110px);
  overflow: auto;
}

.agent,
.conv {
  padding: 10px;
  border: 1px solid #eee;
  border-radius: 12px;
  background: #fafafa;
}

.row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.tab {
  background: #eee;
  color: #000;
}

.tab.on {
  background: #e6f0ff;
  color: #0b66d1;
  border: 2px solid #0b66d1;
}

.preview {
  margin-top: 6px;
  font-size: 12px;
  color: #555;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

select {
  padding: 8px;
  border-radius: 10px;
  border: 1px solid #ccc;
}

.muted {
  color: #777;
  font-size: 12px;
}

.pill {
  padding: 2px 8px;
  border-radius: 999px;
  border: 1px solid #ddd;
  font-size: 11px;
  background: #f5f5f5;
}

.pill.READY {
  background: #e9f6ee;
  border-color: #bfe6cc;
}
.pill.LIVE {
  background: #4fc77d;
  border-color: #abe4be;
  color: #fafafa;
}
.pill.AWAY {
  background: #fff5e6;
  border-color: #ffe0a3;
}
.pill.OFFLINE {
  background: #f5f5f5;
  border-color: #ddd;
}
.pill.OPEN {
  background: #e9f6ee;
  border-color: #bfe6cc;
}
.pill.CLOSED {
  background: #f5f5f5;
  border-color: #ddd;
}
.pill.QUEUED {
  background: #fff5e6;
  border-color: #ffe0a3;
}

.chatContainer {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 110px);
  background: #fff;
}

.chatHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px;
  border-bottom: 1px solid #eee;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #f8f9fb;
}

.msg {
  max-width: 75%;
  margin-bottom: 10px;
}

.agentMsg {
  align-self: flex-end;
  --story-card-bg: #eaf3ff;
  --story-card-border: #cfe0ff;
}

.visitorMsg {
  align-self: flex-start;
  --story-card-bg: #ffffff;
  --story-card-border: #dddddd;
}

.botMsg {
  align-self: flex-end;
  --story-card-bg: #eefaf0;
  --story-card-border: #cfead5;
}

.systemMsg {
  align-self: center;
  background: #f1f1f1;
  max-width: 90%;
  border-radius: 12px;
  border: 1px solid #ddd;
}

.readOnlyBar {
  padding: 12px 14px;
  border-top: 1px solid #eee;
  background: #fafafa;
  color: #666;
  font-size: 13px;
  text-align: center;
}

.emptyState {
  height: calc(100vh - 110px);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #777;
  font-size: 14px;
}


.brand-logo {
  width: 40px;
}


@media (max-width: 900px) {
  .grid {
    grid-template-columns: 1fr;
  }

  .sidebar {
    width: 60px;
    min-width: 60px;
    padding: 10px 8px;
  }

  .grid-btn,
  .icon-btn,
  .avatar {
    width: 38px;
    height: 38px;
  }

  .top {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
}
</style>
